// @ts-nocheck
import axios from "axios";
import { camelCase, get, isObject } from "lodash";
import { parseString as parseXMLString } from "xml2js";

import { Address, AddressResult } from "../../models";

export enum QasProfile {
  AUS = "AUS",
  AUG = "AUG",
}

export enum QasLayout {
  Basic = "Origin",
  Extended = "OriginExtendedAUG",
}

export interface IHeader {
  key: string;
  value: string;
}

export const convertSearchData = (
  query: string,
  qasProfile: QasProfile,
  layout: QasLayout,
  limit: number
) => {
  return [
    `<?xml version='1.0' encoding='UTF-8' ?>`,
    `<soapenv:Envelope xmlns:xsi='http://www.w3.org/2001/XMLSchema-instance' xmlns:xsd='http://www.w3.org/2001/XMLSchema' xmlns:soapenv='http://schemas.xmlsoap.org/soap/envelope/' xmlns='http://www.qas.com/OnDemand-2011-03'>`,
    `<soapenv:Header>`,
    `<QAQueryHeader>`,
    `<QAAuthentication></QAAuthentication>`,
    `<Security></Security>`,
    `</QAQueryHeader>`,
    `</soapenv:Header>`,
    `<soapenv:Body>`,
    `<QASearch Localisation='${qasProfile}' RequestTag=''>`,
    `<Country>${qasProfile}</Country>`,
    `<Engine Flatten='true' Intensity='Close' PromptSet='Default' Threshold='${limit}' Timeout='10000'>Intuitive</Engine>`,
    `<Layout>${layout}</Layout>`,
    `<Search>${query}</Search>`,
    `<FormattedAddressInPicklist>false</FormattedAddressInPicklist>`,
    `</QASearch>`,
    `</soapenv:Body>`,
    `</soapenv:Envelope>`,
  ].join("");
};

export const convertParseData = (
  addressId: string,
  qasProfile: QasProfile,
  qasLayout: QasLayout
) => {
  return [
    `<?xml version='1.0' encoding='UTF-8'?>`,
    `<soapenv:Envelope xmlns:xsi='http://www.w3.org/2001/XMLSchema-instance' xmlns:xsd='http://www.w3.org/2001/XMLSchema' xmlns:soapenv='http://schemas.xmlsoap.org/soap/envelope/' xmlns='http://www.qas.com/OnDemand-2011-03'>`,
    `<soapenv:Body>`,
    `<QAGetAddress Localisation='${qasProfile}' RequestTag=''>`,
    `<Layout>${qasLayout}</Layout>`,
    `<Moniker>${addressId}</Moniker>`,
    `</QAGetAddress>`,
    `</soapenv:Body>`,
    `</soapenv:Envelope>`,
  ].join("");
};

export const createQasAddress = (rawInstance: Object): Address => {
  if (!rawInstance) {
    throw new ReferenceError("Missing required argument `rawInstance`");
  }

  if (!isObject(rawInstance)) {
    throw new TypeError(
      "Argument `rawInstance` was not of required type `Object`"
    );
  }

  return Object.entries(rawInstance).reduce(
    (acc: Address, [key, value]: [string, string]) => {
      switch (key) {
        case "Building level":
          acc.level = value;
          break;
        case "Flat/Unit":
          acc.unit = value;
          break;
        case "Building number":
          acc.streetNumber = value;
          break;
        case "Allotment":
          acc.allotment = value;
          break;
        case "All postal delivery types (Number)":
          acc.allPostalDeliveryTypesNumber = value;
          break;
        case "All postal delivery types (Type)":
          acc.allPostalDeliveryTypesType = value;
          break;
        case "Building name":
          acc.buildingName = value;
          break;
        case "Building name 2":
          acc.buildingName2 = value;
          break;
        case "Flat/Unit (Type)":
          acc.unitType = value;
          break;
        case "Flat/Unit (Number)":
          acc.unitNumber = value;
          break;
        case "Building level (Type)":
          acc.buildingLevelType = value;
          break;
        case "Building level (Number)":
          acc.buildingLevelNumber = value;
          break;
        case "Building number (First)":
          acc.buildingNumberFirst = value;
          break;
        case "Building number (Last)":
          acc.buildingNumberLast = value;
          break;
        case "Allotment (Lot)":
          acc.allotmentLot = value;
          break;
        case "Allotment (Number)":
          acc.allotmentNumber = value;
          break;
        case "Sub-building number":
          acc.subBuildingNumber = value;
          break;
        case "Sub-building number (Alpha)":
          acc.subBuildingNumberAlpha = value;
          break;
        case "Sub-building number (Number)":
          acc.subBuildingNumberNumber = value;
          break;
        case "Street (Name)":
          acc.streetNameOnly = value;
          break;
        case "Street (Type)":
          acc.streetType = value;
          break;
        case "Street (Type Suffix)":
          acc.streetTypeSuffix = value;
          break;
        case "Street":
          acc.streetName = value;
          break;
        case "Locality":
          acc.city = value;
          break;
        case "State code":
          acc.state = value;
          break;
        case "Postcode":
          acc.postcode = value;
          break;
        case "DPID/DID":
          acc.dpid = value;
          break;
        default:
          /**
           * Any keys we weren't expecting to have, we'll
           * still ingest them and give them a seat at the
           * table.
           */
          acc[camelCase(key)] = value;
      }

      return acc;
    },
    {} as Address
  );
};

export const createQasSearchResult = (rawInstance: Object): AddressResult => {
  const getValueFromRawInstance = (key: string): string =>
    rawInstance[key][0] || "";

  if (!rawInstance) {
    throw new ReferenceError("Missing required argument `rawInstance`");
  }

  if (isObject(rawInstance) === false) {
    throw new TypeError(
      "Argument `rawInstance` was not of required type `Object`"
    );
  }

  const addressId = getValueFromRawInstance("Moniker");
  const label = getValueFromRawInstance("PartialAddress").replace(
    /[\s]{2}/gi,
    " "
  );
  const postcode = getValueFromRawInstance("Postcode");

  return {
    addressId,
    label,
    postcode,
  };
};

export const deconstructResultData = (response) => {
  /**
   * This is where the data we care about lives deep down in the `response`
   *
   * @type {string}
   */
  const resultsPropPathRoot =
    "soap:Envelope.soap:Body[0].QASearchResult[0].QAPicklist[0]";

  /**
   * If there's no results for a search, the API does not in fact return an
   * empty list. Instead, it returns one search result, which happens to say
   * "No results". Luckily, the API's response also tells us how many results
   * there are, so we can query that instead of counting the items in the
   * results list.
   *
   * @type {boolean}
   */
  const hasNoResults =
    parseInt(get(response, `${resultsPropPathRoot}.Total`, 0), 10) === 0;

  if (hasNoResults) {
    return [];
  }

  const rawResults = get(response, `${resultsPropPathRoot}.PicklistEntry`, []);

  if (rawResults.length === 0) {
    return [];
  }

  const qasSearchResults = rawResults.map(createQasSearchResult);

  // fix for the QAS payload change, which return payload with <Total>1</Total>
  // Previous payload was <Total>0</Total> when there are no matches.
  // condition added to check for empty result.
  if (qasSearchResults.length === 1) {
    return !(
      qasSearchResults[0].addressId ||
      qasSearchResults[0].postcode ||
      qasSearchResults[0].moniker
    )
      ? []
      : qasSearchResults;
  }

  return qasSearchResults;
};

export function parseDoGetAddressResponse(response) {
  /**
   * This is where the data we care about lives deep down in the `response`
   *
   * @type {string}
   */
  const resultsPropPathRoot =
    "soap:Envelope.soap:Body[0].Address[0].QAAddress[0].AddressLine";

  const rawAddressFragments = get(response, resultsPropPathRoot, []);

  /**
   * This is a crudely-parsed version of the address object, which will be
   * properly parsed by {@link QASAddress.createFromServerFormat} later on.
   * This step is just to ensure we have a well-formed bit of data to work
   * with.
   *
   * @type {Object.<string, (string|null)>}
   */
  const parsedAddress = rawAddressFragments
    .map((unparsedFragment) => {
      return {
        key: unparsedFragment.Label[0],
        value:
          unparsedFragment.Line[0] === ""
            ? /**
               * If the API responds with an empty string as one of the
               * values, let's "upgrade" that to a real bona-fide null.
               */
              null
            : unparsedFragment.Line[0],
      };
    })

    /**
     * Now we collect those arrays of key/value tuples to a single object
     */
    .reduce((acc, addressFragment) => {
      acc[addressFragment.key] = addressFragment.value;
      return acc;
    }, {});

  return createQasAddress(parsedAddress);
}

export const convertSoapXmlToJson = (soap: string) => {
  return new Promise((resolve, reject) => {
    parseXMLString(soap, (err, result) => {
      if (err) {
        reject(err);
      }

      resolve(result);
    });
  });
};

export const qasLookup = (
  authToken: string,
  qasProfile: QasProfile = QasProfile.AUS,
  qasLayout: QasLayout = QasLayout.Basic,
  url: string = "https://ws3.ondemand.qas.com/ProOnDemand/V3/ProOnDemandService.asmx?wsdl",
  customHeaders: IHeader[] = []
) => {
  if (!authToken) {
    throw new ReferenceError("Missing required parameter `authToken`");
  }

  const defaultHeaders = {
    "Auth-Token": authToken,
    Accept: "application/xml,text/xml,*/*;q=0.01",
    "Content-Type": "text/xml; charset=UTF-8",
  };

  customHeaders.forEach((header) => {
    defaultHeaders[header.key] = header.value;
  });

  const fetchAddresses = (query: string): Promise<AddressResult[]> => {
    if (!query) {
      throw new ReferenceError("Missing parameter `query`");
    }

    return axios({
      method: "POST",
      url,
      data: convertSearchData(query, qasProfile, qasLayout, 10),
      headers: {
        ...defaultHeaders,
        SOAPAction: "http://www.qas.com/OnDemand-2011-03/DoSearch",
      },
    }).then(async (response) => {
      const data = await convertSoapXmlToJson(response.data);
      return deconstructResultData(data);
    });
  };

  const fetchAddressDetails = (addressId: string): Promise<Address> => {
    if (!addressId) {
      throw new ReferenceError("Missing required parameter `addressId`");
    }

    if (typeof addressId !== "string") {
      throw new TypeError(
        "Parameter `addressId` was not of required type `String`"
      );
    }

    return axios({
      method: "POST",
      url,
      data: convertParseData(addressId, qasProfile, qasLayout),
      headers: {
        ...defaultHeaders,
        SOAPAction: "http://www.qas.com/OnDemand-2011-03/DoGetAddress",
      },
    })
      .then((response) => {
        if (!response) {
          throw new Error("Missing response");
        }

        if (!response.data) {
          throw new Error("Missing response body");
        }

        return convertSoapXmlToJson(response.data);
      })
      .then(parseDoGetAddressResponse);
  };

  return {
    fetchAddresses,
    fetchAddressDetails,
  };
};
